import React, { useState, useEffect, useMemo } from "react";
import { Wrapper } from "./style";
import { useSelector } from "react-redux";

// components
import Link from "components/common/Link";
import MedicationIcon from "@mui/icons-material/Medication";
import PersonIcon from "@mui/icons-material/Person";
import Register from "components/templates/register";
import Setting from "components/templates/profile/Setting";
import HomeIcon from "@mui/icons-material/Home";
import LoginIcon from "@mui/icons-material/Login";
import EventSeatIcon from "@mui/icons-material/EventSeat";

// Configs && Libs
import Translation from "libs/Translation";
import ModalManager from "libs/ModalManager";
import { appConfig } from "configs";

const MobileMenuBar = ({ webConfig, isMobile, appPromptClose }) => {
  const { user, authentication } = useSelector((state) => state.user);
  const isAuth = authentication?.accessToken ? true : false;

  // 1. Add Mounted check to prevent Hydration Mismatch
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const handleOpenModal = () => {
    if (isAuth) {
      ModalManager.show({
        content: <Setting webConfig={webConfig} />,
        title: Translation.t("title.logged-in-with", {
          phone: Translation.n(`09${user.phone.slice(2)}`),
        }),
        maxWidth: "sm",
        fullScreenMobile: false,
        hideCloseButton: false,
        closable: true,
      });
    } else {
      ModalManager.show({
        content: <Register webConfig={webConfig} />,
        title: "",
        maxWidth: "sm",
        fullScreenMobile: true,
        hideCloseButton: false,
        closable: true,
      });
    }
  };

  // 2. Calculate items dynamically using useMemo
  // This replaces the old useState/useEffect combination that caused the flicker/error
  const items = useMemo(() => {
    // Only show user-specific data if we have mounted AND have a profile
    const showUserMenu = hasMounted && user?.profiles?.length;

    const _items = [
      {
        id: 0,
        navigate: true,
        label: Translation.t("label.home"),
        link: "/",
        params: webConfig.lang !== "fa" ? { lang: webConfig.lang } : {},
        icon: <HomeIcon className="icon" />,
      },
      {
        id: 1,
        navigate: true,
        label: Translation.t("label.doctors-page"),
        link: "/explore/doctors",
        params: webConfig.lang !== "fa" ? { lang: webConfig.lang } : {},
        icon: <MedicationIcon className="icon" />,
      },
      {
        id: 2,
        navigate: true,
        label: Translation.t("label.appointments"),
        link: "/profile/appointments",
        params: webConfig.lang !== "fa" ? { lang: webConfig.lang } : {},
        icon: <EventSeatIcon className="icon" />,
      },
      {
        id: 3,
        navigate: true,
        label: Translation.t("label.for-service-provider"),
        link: `${appConfig.site_url}/hwp`,
        params: webConfig.lang !== "fa" ? { lang: webConfig.lang } : {},
        icon: <LoginIcon className="icon" />,
      },
      {
        id: 4,
        navigate: false,
        label: showUserMenu
          ? user?.profiles[0]?.FirstName
            ? `${user?.profiles[0]?.FirstName} ${user?.profiles[0]?.LastName}`
            : user?.profiles[0]?.phoneNumber
          : Translation.t("label.login-register"),
        onClick: () => handleOpenModal(),
        icon: <PersonIcon className="icon" />,
      },
    ];

    // Logic:
    // If User: [Home, Docs, Appts, Profile] (Indices: 0, 1, 2, 4)
    // If Guest: [Home, Docs, Provider, Login] (Indices: 0, 1, 3, 4)
    if (showUserMenu) {
      return [..._items.slice(0, 3), ..._items.slice(4, 5)];
    } else {
      return [..._items.slice(0, 2), ..._items.slice(3, 5)];
    }
  }, [hasMounted, user, webConfig.lang]); // Dependencies ensure it updates when these change

  return (
    <Wrapper isMobile={isMobile} appPromptClose={appPromptClose}>
      {items.map((i) => (
        <React.Fragment key={i.id}>
          {i.navigate ? (
            <Link to={i.link} params={i.params} className="item">
              {i.icon}
              <span className="label">{i.label}</span>
            </Link>
          ) : (
            <div onClick={i.onClick} className="item">
              {i.icon}
              <span className="label">{i.label}</span>
            </div>
          )}
        </React.Fragment>
      ))}
    </Wrapper>
  );
};

export default MobileMenuBar;
